import React from 'react';
import moment from 'moment';
import { Button } from 'primereact/button';
import { ACTING_AS, AGREEMENT_TYPE } from '../../config/constants';
import { FilterMatchMode } from 'primereact/api';
import {
  dateRangeFilterTemplate,
  dateRangeFilterFunction,
  singleSelectFilterTemplate,
  textFilterTemplate,
} from '../../../../components/shared/DataTable/filterComponents';

export const ROUTES = [
  {
    label: 'special-billing-agreements',
    link: '',
  },
  {
    label: 'additional_functionalities',
    link: 'additional-functionalities',
  },
];

const AGREEMENT_TYPE_OPTIONS = ({ t }) => [
  { label: t('special_billing.Self-billing_col'), value: AGREEMENT_TYPE.SELF },
  {
    label: t('special_billing.Third-Party_billing_col'),
    value: AGREEMENT_TYPE.THIRD,
  },
];

const STATUS_OPTIONS = ({ t }) => [
  { label: t('special_billing.Open'), value: 'Open' },
  { label: t('special_billing.Active'), value: 'Active' },
  { label: t('special_billing.Inactive'), value: 'Inactive' },
];

const REASON_OPTIONS = ({ t }) => [
  { label: t('special_billing.Draft'), value: 'Draft' },
  {
    label: t('special_billing.Pending_Confirmation_Confirm_Party'),
    value: 'Pending_Confirmation_Confirm_Party',
  },
  { label: t('special_billing.Active'), value: 'Active' },
  { label: t('special_billing.Expired'), value: 'Expired' },
  { label: t('special_billing.Deleted'), value: 'Deleted' },
  { label: t('special_billing.Rejected'), value: 'Rejected' },
  {
    label: t('special_billing.Future_Dated_And_Approved'),
    value: 'Future_Dated_And_Approved',
  },
  {
    label: t('special_billing.Change_Of_VATP_Status'),
    value: 'Change_Of_VATP_Status',
  },
];

export const SEARCH_CONFIG = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  referenceNumber: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  agreementType: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  sellerName: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  sellerId: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  buyerName: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  buyerId: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  startDate: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  endDate: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.CUSTOM }],
  },
  status: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  reason: {
    operator: 'and',
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
};

export const COLUMNS = ({ t, navigate, deleteAgreement, user }) => [
  {
    label: 'special_billing.reference-number',
    value: 'referenceNumber',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('special_billing.reference-number') }),
      ),
  },
  {
    label: 'special_billing.agreement-type',
    value: 'agreementType',
    body: (rowData) =>
      rowData?.agreementType === AGREEMENT_TYPE.SELF
        ? t('special_billing.Self-billing_col')
        : t('special_billing.Third-Party_billing_col'),
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, AGREEMENT_TYPE_OPTIONS({ t })),
  },
  {
    label: 'special_billing.seller-name',
    value: 'sellerName',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('special_billing.seller-name') }),
      ),
    body: (rowData) =>
      rowData.isMultiple ? t('multiple') : rowData?.sellerName,
  },
  {
    label: 'special_billing.seller-vat-account-number',
    value: 'sellerId',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', {
          column: t('special_billing.seller-vat-account-number'),
        }),
      ),
    body: (rowData) => (rowData.isMultiple ? t('multiple') : rowData?.sellerId),
  },
  {
    label: 'special_billing.buyer-name',
    value: 'buyerName',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', { column: t('special_billing.buyer-name') }),
      ),
    body: (rowData) =>
      rowData.isMultiple
        ? t('multiple')
        : rowData?.buyerName || rowData?.thirdPartyName,
  },
  {
    label: 'special_billing.buyer-vat-account-number',
    value: 'buyerId',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.CONTAINS,
    filterElement: (options) =>
      textFilterTemplate(
        options,
        t('search_by', {
          column: t('special_billing.buyer-vat-account-number'),
        }),
      ),
    body: (rowData) =>
      rowData.isMultiple
        ? t('multiple')
        : rowData?.buyerId || rowData?.thirdPartyId,
  },
  {
    label: 'special_billing.agreement-start-date',
    value: 'startDate',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: dateRangeFilterFunction,
    body: (rowData) =>
      rowData?.startDate
        ? moment(rowData.startDate).format('DD MMM YYYY')
        : '-',
  },
  {
    label: 'special_billing.agreement-end-date',
    value: 'endDate',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    isDateColumn: true,
    filterMatchMode: FilterMatchMode.CUSTOM,
    filterElement: dateRangeFilterTemplate,
    filterFunction: dateRangeFilterFunction,
    body: (rowData) =>
      rowData?.endDate ? moment(rowData.endDate).format('DD MMM YYYY') : '-',
  },
  {
    label: 'special_billing.status',
    value: 'status',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, STATUS_OPTIONS({ t })),
    body: (rowData) => {
      const color =
        STATUS_COLOR_MAP[rowData?.status] || STATUS_COLOR_MAP.DEFAULT;
      return (
        <span
          style={{
            color,
          }}
        >
          {t(`special_billing.${rowData?.status}`)}
        </span>
      );
    },
  },
  {
    label: 'special_billing.reason',
    value: 'reason',
    sortable: false,
    filter: true,
    showSortInFilter: true,
    filterMatchMode: FilterMatchMode.EQUALS,
    filterElement: (options) =>
      singleSelectFilterTemplate(options, REASON_OPTIONS({ t })),
    body: (rowData) => {
      const color =
        STATUS_COLOR_MAP[rowData?.status] || STATUS_COLOR_MAP.DEFAULT;
      return (
        <span
          style={{
            color,
          }}
        >
          {t(`special_billing.${rowData?.reason}`)}
        </span>
      );
    },
  },
  {
    label: 'special_billing.actions',
    body: (rowData) => {
      const canEdiDraft =
        rowData?.status === 'Open' && rowData?.reason === 'Draft';

      const canAmend = rowData?.status === 'Active';

      // for confirming party ?
      // NBREINV-621
      const canReview =
        rowData?.status === 'Open' &&
        (rowData?.reason === 'Pending_Confirmation_Confirm_Party' ||
          rowData?.reason === 'Pending_Confirmation_Request_Party') &&
        rowData?.createdByUser !== user.userId;

      const canDelete =
        (!canReview &&
          (rowData?.status === 'Open' || rowData?.status === 'Inactive')) ||
        (rowData?.status === 'Inactive' && rowData?.reason === 'Rejected');

      return (
        <div className="d-flex">
          {canReview && (
            <Button
              className="flex-1"
              label={t('special_billing.review')}
              onClick={() => {
                navigate(`review/${rowData.agreementId}`, {
                  state: {
                    data: rowData,
                    id: rowData.agreementId,
                    readOnly: true,
                  },
                });
              }}
            />
          )}
          {canEdiDraft && (
            <Button
              className="flex-1"
              icon={'pi pi-file-edit'}
              onClick={() => {
                let createAs = ACTING_AS.SELLER;
                if (
                  rowData.agreementType === AGREEMENT_TYPE.SELF &&
                  rowData.sellerId !== user.tax_number
                ) {
                  createAs = ACTING_AS.BUYER;
                }

                if (
                  rowData.agreementType === AGREEMENT_TYPE.THIRD &&
                  rowData.sellerId !== user.tax_number
                ) {
                  createAs = ACTING_AS.THIRD;
                }

                navigate(`edit/${rowData.agreementId}`, {
                  state: {
                    ...rowData,
                    createAs: createAs,
                  },
                });
              }}
              text
              link
            />
          )}
          {canAmend && (
            <Button
              className="flex-1"
              icon={'pi pi-file-edit'}
              onClick={() => {
                navigate(`amend/${rowData.agreementId}`, {
                  state: { data: rowData, readOnly: false },
                });
              }}
              text
              link
            />
          )}
          {canDelete && (
            <Button
              className="flex-1"
              icon={'pi pi-trash'}
              onClick={() => {
                deleteAgreement(rowData);
              }}
              text
              link
            />
          )}
        </div>
      );
    },
  },
];

const STATUS_COLOR_MAP = {
  Open: '#D1A000',
  Active: '#2CB41A',
  Inactive: '#FF3232',
};
